import React, { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/router';
import { enableScrolling, handleExternalLinkClick, updateLinksHref, updateLinksHrefNoLinksWarning } from 'lib/utils';
import RichTextWrapper from 'components/helpers/RichText/RichTextWrapper';
import Modal from 'components/helpers/Modal';
import { ExternalLinksWarningModalProps } from './ExternalLinkWarningTypes';

/**
 * A Front-end only implementation of the Modal component.
 *
 * @example
 * ```tsx
 * <ExternalLinksWarningModal warningFields={warningFields} />
 * ```
 *
 * @param props - ExternalLinksWarningModalProps.
 * @returns A JSX element.
 */
export const ExternalLinksWarningModal: React.FC<ExternalLinksWarningModalProps> = (props) => {
  const { warningFields } = props;

  const [showModal, setShowModal] = useState(false);
  const [linkTarget, setLinkTarget] = useState('');
  const [navigationUrl, setNavigationUrl] = useState('');
  const router = useRouter();
  const addExternalLinksWarningModalDialog = warningFields && warningFields.text ? true : false;
  const whiteListDomains = warningFields && warningFields.whiteListDomains ? warningFields.whiteListDomains.value : '';

  const onExternalLinkClick = (url: string, target?: string | null): void => {
    setLinkTarget(target || '');
    enableScrolling(false);
    setNavigationUrl(url);
    setShowModal(true);
  };

  const onLinkClick = useCallback(
    (url: string, target?: string | null): void => {
      if (target === '_blank') {
        window.open(url);
      } else {
        router.push(url);
      }
    },
    [router]
  );

  useEffect(() => {
    const handleLinkClick = (event: MouseEvent) => {
      handleExternalLinkClick(event, whiteListDomains, onExternalLinkClick, onLinkClick);
    };

    const updateLinksHrefRef = addExternalLinksWarningModalDialog ? updateLinksHref : updateLinksHrefNoLinksWarning;

    if (addExternalLinksWarningModalDialog) {
      window.addEventListener('load', updateLinksHrefRef);
      document.addEventListener('click', handleLinkClick);
      router.events.on('routeChangeComplete', updateLinksHrefRef);
    } else {
      window.addEventListener('load', updateLinksHrefRef);
      router.events.on('routeChangeComplete', updateLinksHrefRef);
    }

    return () => {
      window.removeEventListener('load', updateLinksHrefRef);
      document.removeEventListener('click', handleLinkClick);
      router.events.off('routeChangeComplete', updateLinksHrefRef);
    };
  }, [router, setNavigationUrl, setShowModal, addExternalLinksWarningModalDialog, whiteListDomains, onLinkClick]);

  return addExternalLinksWarningModalDialog ? (
    <Modal
      opened={showModal}
      title={warningFields?.title?.value || ''}
      okButtonLabel={warningFields?.okButton?.value || ''}
      closeButtonLabel={warningFields?.cancelButton?.value || ''}
      onOKCallback={() => {
        enableScrolling();
        onLinkClick(navigationUrl, linkTarget);
      }}
      onCloseCallback={() => {
        enableScrolling();
        setShowModal(false);
      }}
    >
      <RichTextWrapper field={{ value: warningFields?.text?.value || '' }} />
    </Modal>
  ) : (
    <></>
  );
};

ExternalLinksWarningModal.displayName = 'ExternalLinksWarningModal';

export default ExternalLinksWarningModal;
